import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, BUILD_TAG } from "@/App";
import { ArrowRight, Smartphone } from "lucide-react";
import Logo from "@/components/Logo";
import LandingHero from "@/components/LandingHero";
import InstallAppBanner from "@/components/InstallAppBanner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const REDIRECT_URL = encodeURIComponent(window.location.origin);
const OAUTH_BASE_URL = process.env.REACT_APP_OAUTH_URL || "https://auth.emergentagent.com";
const AUTH_URL = `${OAUTH_BASE_URL}/?redirect=${REDIRECT_URL}`;

export default function Landing() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-app">
      <InstallAppBanner />

      {/* Top bar */}
      <header className="border-b border-line bg-app/80 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto max-w-6xl px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo variant="icon" size={28} />
            <span className="text-[15px] font-semibold tracking-tight text-ink">Golf Eclectic</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate("/register")} className="ge-btn-ghost text-[13px]" data-testid="register-link">New here?</button>
            <button onClick={() => navigate("/login")} className="ge-btn-primary text-[13px]" data-testid="signin-link">
              Sign in <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="container mx-auto max-w-6xl px-5 sm:px-8">
        <section className="grid lg:grid-cols-12 gap-8 lg:gap-16 pt-12 sm:pt-20 pb-12 sm:pb-20">
          <div className="lg:col-span-7">
          <p className="ge-eyebrow mb-4">Golf Eclectic · multi-round format</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] text-ink leading-[1.05]">
              Build your perfect round, <span className="text-accent">one hole at a time.</span>
            </h1>
            <p className="text-[17px] sm:text-lg text-ink-muted mt-6 max-w-2xl leading-relaxed">
              Eclectic golf rewards your best on every hole, across every round of the competition. Track your improvements, see where you stand against the field, and watch your perfect round come together.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-2.5">
              {/* Primary CTA — Sign in. Most visitors are returning players, so this is
                  the visually dominant button on the page. */}
              <button onClick={() => navigate("/login")} className="ge-btn-primary !px-5 !py-3 !text-[14.5px] justify-center" data-testid="hero-signin-btn">
                Sign in <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => (window.location.href = AUTH_URL)} className="ge-btn-outline !px-5 !py-3 !text-[14.5px] justify-center" data-testid="hero-google-btn">
                Continue with Google
              </button>
            </div>
            <p className="mt-3 text-[13px] text-ink-muted">
              New here?{" "}
              <button onClick={() => navigate("/register")} className="text-accent hover:text-accent-deep underline font-medium" data-testid="hero-register-link">
                Create an account
              </button>
              {" · "}
              <button onClick={() => window.dispatchEvent(new Event("pwa-show-install-guide"))} className="text-accent hover:text-accent-deep underline inline-flex items-center gap-1" data-testid="hero-pwa-link">
                <Smartphone className="w-3 h-3" /> Add to phone
              </button>
            </p>
          </div>

          {/* Rotating real-app preview — eclectic scorecard with HIO + Excellent 2,
              player dashboard, competition summary. Code-rendered so it stays in
              sync with brand tokens (no stock screenshot maintenance burden). */}
          <div className="lg:col-span-5">
            <LandingHero />
          </div>
        </section>

        {/* "How it works" / numbered explainer */}
        <section className="border-y border-line py-12 sm:py-16 -mx-5 sm:-mx-8 px-5 sm:px-8">
          <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto">
            {[
              { n: "01", t: "Play your rounds", b: "Enter each scorecard from the course or after your round. We track every hole." },
              { n: "02", t: "Beat your best", b: "Each round, only holes you improved on update your eclectic score." },
              { n: "03", t: "Climb the leaderboard", b: "Net countback decides ties. Watch your perfect round build through the competition." },
            ].map((s) => (
              <div key={s.n}>
                <p className="font-mono tabular-nums text-[12px] text-accent font-semibold tracking-wider">{s.n}</p>
                <h3 className="text-[19px] font-semibold tracking-tight text-ink mt-2">{s.t}</h3>
                <p className="text-[14.5px] text-ink-muted mt-2 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Quote / soft pitch */}
        <section className="py-12 sm:py-16 max-w-4xl">
          <p className="ge-eyebrow mb-3">Built for clubs and golf societies</p>
          <p className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink leading-snug">
            One scoring system. Multi-tee, mens & ladies, 9 or 18, with countback baked in.
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-line">
        <div className="container mx-auto max-w-6xl px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-3 text-[12px] text-ink-soft">
          <span>© 2026 Golf Eclectic</span>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/legal/privacy")} className="hover:text-ink">Privacy</button>
            <button onClick={() => navigate("/legal/terms")} className="hover:text-ink">Terms</button>
            <button onClick={() => navigate("/admin/login")} className="hover:text-ink">Admin</button>
          </div>
        </div>
        {/* Build tag — kept tiny and on its own line at the very bottom so it
            never pushes the Privacy/Terms/Admin row to two lines on mobile. */}
        <div className="container mx-auto max-w-6xl px-5 sm:px-8 pb-4">
          <p className="text-center text-[10px] font-mono text-ink-soft/50" data-testid="build-tag">
            build {BUILD_TAG}
          </p>
        </div>
      </footer>
    </div>
  );
}
